import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';

/**
 * The one question a city tap on the home page asks: what are you looking for
 * there? The answer only picks which list opens — nothing is stored or sent.
 *
 * Each choice is a real link, so keyboard, middle-click and "open in new tab"
 * work without any handler, and the page behind needs no state beyond which
 * city was tapped. Hospital goes to /doctors/:city because that page is the
 * city's hospitals, each with its doctors; Doctor and Scan or test open the
 * /alldoctor search with the city typed in, the same way the app does.
 *
 * ponytail: Scan or test lands on the Scan Centres tab; the Blood Tests tab is
 * one tap away there. Split the choice in two if blood tests turn out to be
 * what most people mean.
 */
const enc = encodeURIComponent;
const OPTIONS = [
  { key: 'doctor',   icon: 'bi-clipboard-pulse', to: (c) => `/alldoctor?q=${enc(c.name)}` },
  { key: 'scan',     icon: 'bi-activity',        to: (c) => `/alldoctor?kind=SCAN_CENTER&q=${enc(c.name)}` },
  { key: 'hospital', icon: 'bi-hospital',        to: (c) => `/doctors/${c.slug}` },
];

export default function CityIntent({ city, onClose }) {
  const { t } = useTranslation();
  const sheet = useRef(null);

  // Esc closes; focus moves into the dialog so the keyboard starts there.
  useEffect(() => {
    sheet.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="ci-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{css}</style>
      <div className="ci-sheet" role="dialog" aria-modal="true" aria-labelledby="ci-title" tabIndex={-1} ref={sheet}>
        <button type="button" className="ci-close" onClick={onClose} aria-label={t('hero.intent.close')}>✕</button>
        <h2 id="ci-title" className="ci-title">{t('hero.intent.title', { city: city.name })}</h2>
        {OPTIONS.map((o) => (
          <Link key={o.key} to={o.to(city)} className="ci-opt">
            <i className={`bi ${o.icon} ci-opt-icon`} aria-hidden="true" />
            <span className="ci-opt-label">{t(`hero.intent.${o.key}`)}</span>
            <i className="bi bi-arrow-right" aria-hidden="true" />
          </Link>
        ))}
      </div>
    </div>,
    document.body,
  );
}

const css = `
.ci-backdrop { position: fixed; inset: 0; z-index: 1080; display: flex; align-items: center; justify-content: center; padding: 16px; background: rgba(15,23,42,0.55); }
.ci-sheet { position: relative; width: 100%; max-width: 380px; background: #fff; border-radius: 18px; padding: 22px 18px 18px; box-shadow: 0 24px 64px rgba(15,23,42,0.35); outline: none; }
.ci-close { position: absolute; top: 10px; right: 10px; width: 32px; height: 32px; border: 0; border-radius: 50%; background: #F1F5F9; color: #475569; cursor: pointer; }
.ci-title { font-size: 1.1rem; font-weight: 700; margin: 0 36px 14px 2px; color: #0F172A; }
.ci-opt { display: flex; align-items: center; gap: 12px; padding: 14px; margin-top: 10px; border: 1px solid var(--blue-100, #DBEAFE); border-radius: 14px; color: #0F172A; text-decoration: none; font-weight: 600; }
.ci-opt:hover, .ci-opt:focus-visible { border-color: var(--blue-600, #1565C0); background: #F8FAFF; }
.ci-opt-icon { color: var(--blue-600, #1565C0); font-size: 20px; }
.ci-opt-label { flex: 1; }
`;
